import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { renderRichText as renderStoryblokRichText } from '@storyblok/richtext/static';
import { SeoService } from 'src/app/core/services/seo.service';
import { CloudService } from 'src/app/services/cloud.service';

interface CaseStudyDetailView {
  id: string;
  clientName: string;
  title: string;
  tag: string;
  excerpt: string;
  executiveSummary: string;
  keyOutcomes: string[];
}

@Component({
  selector: 'app-case-study-details',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './case-study-details.component.html',
  styleUrls: ['./case-study-details.component.css']
})
export class CaseStudyDetailsComponent implements OnInit, OnDestroy {

  caseStudy: CaseStudyDetailView | null = null;
  renderedBody = '';
  loading = false;
  errorMessage = '';
  private routeSub?: Subscription;
  private fetchSub?: Subscription;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cloudService: CloudService,
    private readonly seoService: SeoService
  ) { }

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe((params) => {
      const routeId = (params.get('id') || '').trim();
      this.loadCaseStudy(routeId);
      this.scrollToTop();
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.fetchSub?.unsubscribe();
  }

  navigateBack(): void {
    this.router.navigate(['/case-study']);
    this.scrollToTop();
  }

  private loadCaseStudy(routeId: string): void {
    if (!routeId) {
      this.loading = false;
      this.errorMessage = 'Invalid case study URL.';
      this.caseStudy = null;
      this.renderedBody = '';
      return;
    }
    this.loading = true;
    this.errorMessage = '';
    this.caseStudy = null;
    this.renderedBody = '';
    this.fetchSub?.unsubscribe();

    this.fetchSub = this.cloudService.getCaseStudyDetails(routeId).subscribe({
      next: (data: any) => {
        const story = data?.object?.story || data?.story || null;
        this.caseStudy = this.mapCaseStudyDetail(story, routeId);
        this.renderRichText(story?.content?.body, this.caseStudy?.excerpt || '');
        if (this.caseStudy) {
          this.updateSeoMetadata(this.caseStudy, routeId, story);
        } else {
          this.errorMessage = 'This case study is currently unavailable.';
        }
        this.loading = false;
      },
      error: (e) => {
        this.loading = false;
        this.errorMessage = e?.error?.message || 'Unable to load case study details.';
        this.caseStudy = null;
        this.renderedBody = '';
      }
    });
  }

  private mapCaseStudyDetail(story: any, routeId: string): CaseStudyDetailView | null {
    if (!story) {
      return null;
    }

    const content = story?.content ?? {};
    const excerpt = this.normalizeText(content?.short_description || 'No description available.');
    const executiveSummary = this.normalizeText(content?.executive_summary || excerpt);

    return {
      id: routeId || String(story?.slug || story?.uuid || story?.id || ''),
      clientName: this.normalizeText(content?.client || 'Enterprise Client'),
      title: this.normalizeText(content?.heading || story?.name || 'Untitled Case Study'),
      tag: this.normalizeText(content?.category || 'Case Study'),
      excerpt,
      executiveSummary,
      keyOutcomes: this.extractKeyOutcomes(content?.key_outcomes)
    };
  }

  private extractKeyOutcomes(doc: any): string[] {
    const nodes = Array.isArray(doc?.content) ? doc.content : [];
    const outcomes: string[] = [];
    const walk = (node: any): void => {
      if (!node) {
        return;
      }
      if (node?.type === 'list_item') {
        const text = this.extractNodeText(node);
        if (text) {
          outcomes.push(text);
        }
        return;
      }
      const content = Array.isArray(node?.content) ? node.content : [];
      content.forEach((child: any) => walk(child));
    };
    nodes.forEach((node: any) => walk(node));
    return outcomes;
  }

  private extractNodeText(node: any): string {
    if (!node) {
      return '';
    }
    if (typeof node?.text === 'string') {
      return this.normalizeText(node.text);
    }
    const content = Array.isArray(node?.content) ? node.content : [];
    return this.normalizeText(content.map((child: any) => this.extractNodeText(child)).join(' '));
  }

  private renderRichText(body: any, fallbackText: string): void {
    try {
      this.renderedBody = renderStoryblokRichText(body || { type: 'doc', content: [] });
      if (!this.renderedBody && fallbackText) {
        this.renderedBody = `<p>${fallbackText}</p>`;
      }
    } catch {
      this.renderedBody = fallbackText ? `<p>${fallbackText}</p>` : '';
    }
  }

  private updateSeoMetadata(caseStudy: CaseStudyDetailView, routeId: string, story: any): void {
    const content = story?.content ?? {};
    const routeUrl = `/case-study/${routeId}`;

    this.seoService.updateRouteSeo(routeUrl, {
      title: this.normalizeText(content?.meta_title || caseStudy.title),
      description: this.normalizeText(content?.meta_description || caseStudy.excerpt),
      type: 'website',
      breadcrumbs: [
        { name: 'Home', url: 'https://www.reve.cloud/' },
        { name: 'Case Studies', url: 'https://www.reve.cloud/case-study' },
        { name: caseStudy.title, url: `https://www.reve.cloud${routeUrl}` }
      ]
    });
  }

  private normalizeText(value: unknown): string {
    return String(value ?? '')
      .replace(/\u00a0/g, ' ')
      .replace(/—/g, '-')
      .replace(/–/g, '-')
      .replace(/’/g, "'")
      .replace(/“|”/g, '"')
      .replace(/Rêve/g, 'Reve')
      .replace(/ê/g, 'e')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private scrollToTop(): void {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }

}